import {
  IGroupedTaskParticipants,
  ITaskOrganizationParticipant,
  ITaskUserParticipant,
  TTaskParticipant,
} from "models/tasks.models";
import {
  INVITATION_TASKS_ALERT_STORAGE_KEY,
  RESOLVED_TASKS_STATUSES,
  TaskStatus,
} from "utils/constants/tasks.constants";

export const isTaskActive = (status: TaskStatus): boolean =>
  !RESOLVED_TASKS_STATUSES.includes(status);

export const checkIfTaskParticipantIsUser = (
  participant: TTaskParticipant,
): participant is ITaskUserParticipant =>
  (participant as ITaskUserParticipant)?.firstName !== undefined &&
  (participant as ITaskUserParticipant)?.lastName !== undefined;

export const checkIfTaskParticipantIsUserGroup = (
  participant: TTaskParticipant,
): participant is IGroupedTaskParticipants[] => Array.isArray(participant);

export const checkIfTaskParticipantIsOrganization = (
  participant: TTaskParticipant,
): participant is ITaskOrganizationParticipant =>
  (participant as ITaskOrganizationParticipant)?.name !== undefined;

type TInvitationTasksAlertStorageState = {
  activeFirstSession?: boolean;
  dismissed?: boolean;
  hasShown?: boolean;
  seen?: boolean;
};

type TInvitationTasksAlertStorageMap = Record<
  string,
  TInvitationTasksAlertStorageState
>;

export const getInvitationTasksAlertStorageKey = () =>
  INVITATION_TASKS_ALERT_STORAGE_KEY;

const getInvitationTasksAlertStorageMap = (
  storage: Storage,
): TInvitationTasksAlertStorageMap => {
  try {
    const stored = storage.getItem(INVITATION_TASKS_ALERT_STORAGE_KEY);

    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error("Error reading invitation tasks alert storage:", error);

    return {};
  }
};

const setInvitationTasksAlertStorageMap = (
  storage: Storage,
  storageMap: TInvitationTasksAlertStorageMap,
) => {
  try {
    storage.setItem(
      INVITATION_TASKS_ALERT_STORAGE_KEY,
      JSON.stringify(storageMap),
    );
  } catch (error) {
    console.error("Error saving invitation tasks alert storage:", error);
  }
};

const updateInvitationTasksAlertStorageForUser = (
  storage: Storage,
  userId: string,
  state: TInvitationTasksAlertStorageState,
) => {
  const storageMap = getInvitationTasksAlertStorageMap(storage);

  setInvitationTasksAlertStorageMap(storage, {
    ...storageMap,
    [userId]: {
      ...storageMap[userId],
      ...state,
    },
  });
};

export const initializeInvitationTasksAlertSession = (
  userId: string,
): boolean => {
  const localStorageMap = getInvitationTasksAlertStorageMap(localStorage);
  const sessionStorageMap = getInvitationTasksAlertStorageMap(sessionStorage);

  if (!localStorageMap[userId]?.seen) {
    updateInvitationTasksAlertStorageForUser(localStorage, userId, {
      seen: true,
    });
    updateInvitationTasksAlertStorageForUser(sessionStorage, userId, {
      activeFirstSession: true,
    });

    return true;
  }

  return sessionStorageMap[userId]?.activeFirstSession === true;
};

export const hasDismissedInvitationTasksAlert = (userId: string): boolean =>
  getInvitationTasksAlertStorageMap(sessionStorage)[userId]?.dismissed === true;

export const dismissInvitationTasksAlert = (userId: string) => {
  updateInvitationTasksAlertStorageForUser(sessionStorage, userId, {
    dismissed: true,
  });
};

export const hasShownInvitationTasksAlert = (userId: string): boolean =>
  getInvitationTasksAlertStorageMap(sessionStorage)[userId]?.hasShown === true;

export const setInvitationTasksAlertShown = (userId: string) => {
  updateInvitationTasksAlertStorageForUser(sessionStorage, userId, {
    hasShown: true,
  });
};

export const clearInvitationTasksAlertSessionStorage = () => {
  sessionStorage.removeItem(INVITATION_TASKS_ALERT_STORAGE_KEY);
};
